import App from "./App.vue";
import router from "./router";
import { setupStore } from "@/store";
import { useI18n } from "@/plugins/i18n";
import { getPlatformConfig } from "./config";
import { MotionPlugin } from "@vueuse/motion";
// import { useEcharts } from "@/plugins/echarts";
import { createApp, type Directive } from "vue";
import { useVxeTable } from "@/plugins/vxeTable";
import { useElementPlus } from "@/plugins/elementPlus";
import { injectResponsiveStorage } from "@/utils/responsive";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { useUserStoreHook } from "@/store/modules/user";
import { useLiveRoomStore } from "@/store/modules/liveRoom/index";
import { useGlobalConfigStore } from "@/store/modules/globalConfig";
import VueTelInput from "vue-tel-input";
import "vue-tel-input/vue-tel-input.css";
// 导入内存监控工具
import "@/utils/memoryMonitor";
// 导入全局余额同步初始化器
import { initGlobalBalanceSync } from "@/utils/emitter/init/globalBalanceInit";
// 导入token过期处理器
import { tokenExpiredHandler } from "@/utils/tokenExpiredHandler";

import Table from "@pureadmin/table";
import PureDescriptions from "@pureadmin/descriptions";

// 引入重置样式
import "./style/reset.scss";
// 导入公共样式
import "./style/index.scss";
// 一定要在main.ts中导入tailwind.css，防止vite每次hmr都会请求src/style/index.scss整体css文件导致热更新慢的问题
import "./style/tailwind.css";
import "element-plus/dist/index.css";
// 导入字体图标
import "./assets/iconfont/iconfont.js";
import "./assets/iconfont/iconfont.css";
// 导入基础响应式样式（已修改为只包含必要样式）
import "./style/responsive.css";
import "animate.css";
// 引入 tippy
import "tippy.js/dist/tippy.css";
import VueTippy from "vue-tippy";

// 轻量级客户端日志器（保存到本地，桌面可视化，移动端不弹窗）
// import "@/utils/clientLogger";

const app = createApp(App);

// 自定义指令
import * as directives from "@/directives";
Object.keys(directives).forEach(key => {
  app.directive(key, (directives as { [key: string]: Directive })[key]);
});

// 全局注册`@iconify/vue`图标库
import {
  IconifyIconOffline,
  IconifyIconOnline,
  FontIcon
} from "./components/ReIcon";
app.component("IconifyIconOffline", IconifyIconOffline);
app.component("IconifyIconOnline", IconifyIconOnline);
app.component("FontIcon", FontIcon);
app.component("vue-tel-input", VueTelInput);
// 全局注册按钮级别权限组件
// import { Auth } from "@/components/ReAuth";
// app.component("Auth", Auth);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

(async () => {
  app.use(pinia);
  setupStore(app);
  app.use(router);
  app.use(VueTippy);

  const config = await getPlatformConfig(app);
  injectResponsiveStorage(app, config);

  app
    .use(MotionPlugin)
    .use(useI18n)
    .use(useElementPlus)
    .use(Table)
    .use(useVxeTable)
    .use(PureDescriptions)
    .use(VueTelInput);

  // 恢复用户信息，而不是重置
  useUserStoreHook().restoreUserInfo();

  // 初始化直播间store，恢复直播间状态
  const liveRoomStore = useLiveRoomStore();
  liveRoomStore.initStore();

  // 新增：检查是否在直播间页面，如果是，延迟恢复播放状态
  setTimeout(() => {
    const currentPath = window.location.pathname;
    const roomIdMatch = currentPath.match(/\/myLiveRoom\/(.+)/);
    if (roomIdMatch && roomIdMatch[1]) {
      const roomId = roomIdMatch[1];
      console.log(`应用启动时检测到直播间页面，房间ID: ${roomId}`);

      // 再次延迟，确保所有系统初始化完成
      setTimeout(() => {
        const state = liveRoomStore.roomStates.get(roomId);
        if (state && state.productList?.status === "live") {
          console.log(`应用启动时恢复直播间${roomId}的播放状态`);
          liveRoomStore.restoreRoomPlaybackAfterRefresh(roomId);
        }
      }, 3000);
    }
  }, 1000);

  // 🚀 新增：初始化全局配置Store
  const globalConfigStore = useGlobalConfigStore();
  globalConfigStore.fetchConfigs();

  // 🌐 初始化全局开播通知和余额同步
  initGlobalBalanceSync();

  // 🔐 启动token过期检查
  tokenExpiredHandler.startTokenExpirationCheck();

  // 添加路由守卫，监听路由变化
  // 路由守卫已在router/index.ts中统一处理，此处移除重复代码

  app.mount("#app");
  // 🍎 苹果端：全局一次性用户手势绑定，确保 keeper 能在前台手势内启动
  try {
    const ua = navigator.userAgent || "";
    const appleLike =
      /iPhone|iPad|iPod/i.test(ua) ||
      (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1) ||
      (/Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua));
    if (appleLike) {
      const liveRoomStore = useLiveRoomStore();
      let started = false;
      const starter = () => {
        if (started) return;
        try {
          const rid =
            liveRoomStore.currentRoomId ||
            window.location.pathname.match(/\/myLiveRoom\/(.+)/)?.[1] ||
            "";
          if (rid) {
            const anyStore: any = liveRoomStore as any;
            // 移除 HTML5 链预热，iOS 侧走 WA-only
            started = true;
          }
        } catch {}
      };
      document.addEventListener("touchend", starter, {
        passive: true,
        once: true
      });
      document.addEventListener("click", starter, {
        passive: true,
        once: true
      });
    }
  } catch {}
})();
